import { PleasedChat } from '../PleasedChat/PleasedChat';
import type { PleasedChatRequestMetadata } from '../PleasedChat/PleasedChat.types';
import type { VoiceChatOptions } from '../PleasedVoice/PleasedVoiceChat.types';
import { PleasedVoiceChat } from '../PleasedVoice/PleasedVoiceChat';
import { setConfig as setApiConfig } from '../PleasedApi';
import type { VisitorDetail } from '../common/PleasedSdk.types';
import type { SystemMessageConfig } from '@/common/api/models.pleased';
import { devLog } from '@/common/debug';

import type { PleasedSdkConfig } from './client.config';
import * as SdkConfig from './client.config';

const chatStore: Record<string | number, PleasedChat> = {};
const voiceStore: Record<string | number, PleasedVoiceChat> = {};

/**
 * Create a new chat request.
 * @param options Chat Request
 */
export function chat(options: PleasedChatRequestMetadata) {
  const chat = new PleasedChat(options);
  chat.once('chat:ready', () => (chatStore[chat.id] = chat));

  return chat;
}

/**
 * Loads an existing chat (for AI intervention or resuming a chat)
 * @param payload - Configuration object
 * @param payload.chatId - The chat ID
 * @param payload.visitor - Visitor details
 * @param payload.firebaseToken - Firebase token for authentication
 * @param payload.timeSinceTriggers - System message triggers
 * @param payload.generateSessionId - If true, generates a new resumable session ID
 * @param payload.skipFirebaseSetup - If true, Firebase setup will be skipped and must be called manually
 * @returns PleasedChat instance
 */
export async function loadExistingChat(payload: {
  chatId: number;
  visitor: VisitorDetail;
  firebaseToken: string;
  timeSinceTriggers?: SystemMessageConfig[];
  generateSessionId?: boolean;
  skipFirebaseSetup?: boolean;
}) {
  devLog.debug('[PLEASED_CLIENT] Loading existing chat', payload);
  const chat = await PleasedChat.loadExistingChat(payload);
  devLog.debug('[PLEASED_CLIENT] Existing chat loaded', chat);
  chatStore[payload.chatId] = chat;
  devLog.debug('[PLEASED_CLIENT] Chat store updated', chatStore);
  return chat;
}

export function get(kind: 'voice', id: string | number): PleasedVoiceChat | null;
export function get(kind: 'chat', id: string | number): PleasedChat | null;
export function get(kind: 'voice' | 'chat', id: string | number): PleasedChat | PleasedVoiceChat | null {
  if (kind === 'chat') return chatStore[id];
  return voiceStore[id];
}

export const isVoiceSupported = async () => await import('@twilio/voice-sdk').then(({ Device }) => Device.isSupported);

export function voice(options: VoiceChatOptions) {
  const chat = new PleasedVoiceChat(options);
  chat.once('voice:created', () => {
    voiceStore[chat.id] = chat;
  });

  return chat;
}

export function setConfig(env: Partial<PleasedSdkConfig>) {
  SdkConfig.setConfig(env);
  setApiConfig(env);
}

export const getConfig = SdkConfig.get;
